/**
 * Email one-time-code sign-in.
 *
 * Requesting a code never says whether the address is known — the API answers
 * identically either way — so the form always advances to the code step. The
 * resend button honours the same cooldown the API enforces, which protects the
 * SES reputation that every OTP depends on.
 *
 * Both steps share one `<form>` element, and the Turnstile widget keeps the same
 * position inside it, so moving between them never unmounts the widget. That
 * matters because the resend on the code step needs its own fresh token: a
 * Turnstile token is single use, and Cloudflare refuses a spent one every time.
 * The widget is therefore reset after every settled request, successful or not,
 * rather than after failures alone.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { OTP_LENGTH, OTP_RESEND_COOLDOWN_SEC } from '@pbtv/shared';
import { TurnstileWidget } from '../components/TurnstileWidget.js';
import type { TurnstileStatus } from '../components/TurnstileWidget.js';
import { errorMessage, isApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { configuredSiteKey } from '../lib/turnstile.js';

type Step = 'email' | 'code';

export function LoginPage(): ReactElement {
  const { isSignedIn, lastSignOutReason, requestSignInCode, signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const destination = useMemo(() => {
    const state = location.state as { from?: string } | null;
    const from = state?.from;
    return from !== undefined && from.startsWith('/') && !from.startsWith('/login') ? from : '/';
  }, [location.state]);

  const siteKey = useMemo(() => configuredSiteKey(), []);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [cooldownSec, setCooldownSec] = useState(0);

  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileStatus, setTurnstileStatus] = useState<TurnstileStatus>('loading');
  const [turnstileResets, setTurnstileResets] = useState(0);

  const clearToken = useCallback(() => setTurnstileToken(''), []);

  const requestCode = useMutation({
    mutationFn: (address: string) => requestSignInCode(address, turnstileToken),
    onSuccess: () => {
      setStep('code');
      setCooldownSec(OTP_RESEND_COOLDOWN_SEC);
    },
    onError: (failure: unknown) => {
      // The server knows better than the shared constant does how long this
      // address is actually locked out for.
      if (isApiError(failure) && failure.retryAfterSec !== null) {
        setCooldownSec(failure.retryAfterSec);
      }
    },
    onSettled: () => {
      // The token has been spent whether or not the request succeeded.
      setTurnstileToken('');
      setTurnstileResets((value) => value + 1);
    },
  });

  const submitCode = useMutation({
    mutationFn: (entered: string) => signIn(email, entered),
    onSuccess: () => navigate(destination, { replace: true }),
  });

  useEffect(() => {
    if (cooldownSec <= 0) return;
    const id = window.setInterval(() => setCooldownSec((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(id);
  }, [cooldownSec]);

  if (isSignedIn) {
    return <Navigate to={destination} replace />;
  }

  const canRequest =
    siteKey !== null && turnstileToken !== '' && !requestCode.isPending && email.trim() !== '';

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (step === 'code') {
      submitCode.mutate(code);
      return;
    }

    // Normalise before sending, and keep the normalised form, so the address
    // shown on the code step is the one the code was actually sent to.
    const address = email.trim().toLowerCase();
    setEmail(address);
    requestCode.mutate(address);
  };

  // On the code step both mutations can fail: entering a wrong code, and asking
  // for a new one. Resending is rate limited per address and per day, so its
  // rejection is an ordinary outcome rather than an edge case — showing only the
  // verify error would leave the resend button greying out and silently coming
  // back with nothing said.
  const failure = step === 'email' ? requestCode.error : (submitCode.error ?? requestCode.error);

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="brand-mark" aria-hidden="true">
            PB
          </span>
          <span className="brand-name">
            Pizza Bros <span className="brand-name__accent">TV</span>
          </span>
        </div>

        <h1 className="login__title">{step === 'email' ? 'Sign in' : 'Check your email'}</h1>
        <p className="login__subtitle">
          {step === 'email'
            ? 'We email a six-digit code to your work address. There is no password to remember or leak.'
            : `If ${email} has an account, a ${OTP_LENGTH}-digit code is on its way. It expires in ten minutes.`}
        </p>

        {lastSignOutReason === 'session-expired' && step === 'email' ? (
          <p className="alert alert--warning" role="status">
            Your session ended, so you were signed out. Sign in again to pick up where you left off.
          </p>
        ) : null}

        <form className="login__form" onSubmit={handleSubmit} noValidate>
          {step === 'email' ? (
            <label className="field" key="email-field">
              <span className="field__label">Work email</span>
              <input
                className="input"
                type="email"
                name="email"
                autoComplete="email"
                autoFocus
                required
                placeholder="you@pizzabros.co.nz"
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
            </label>
          ) : (
            <label className="field" key="code-field">
              <span className="field__label">Six-digit code</span>
              <input
                className="input input--code"
                type="text"
                name="one-time-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                maxLength={OTP_LENGTH}
                placeholder="000000"
                value={code}
                onChange={(event) =>
                  setCode(event.currentTarget.value.replace(/\D/g, '').slice(0, OTP_LENGTH))
                }
              />
            </label>
          )}

          {/*
            Held in the same slot on both steps so the widget survives the step
            change. Unmounting it would cost a fresh challenge every time
            somebody went back to correct their address.
          */}
          {siteKey === null ? (
            <p className="alert alert--danger" role="alert" key="check">
              This build has no Cloudflare Turnstile site key, so the check cannot load and the API
              will refuse to send a code. Set <span className="mono">VITE_TURNSTILE_SITE_KEY</span>{' '}
              in the deployment and rebuild.
            </p>
          ) : (
            <TurnstileWidget
              key="check"
              siteKey={siteKey}
              resetSignal={turnstileResets}
              onToken={setTurnstileToken}
              onClear={clearToken}
              onStatusChange={setTurnstileStatus}
              label={step === 'email' ? 'Human check' : 'Human check, for a resend'}
            />
          )}

          {step === 'email' ? (
            <button
              key="submit"
              type="submit"
              className="button button--primary button--block"
              disabled={!canRequest}
            >
              {requestCode.isPending ? 'Sending…' : 'Email me a code'}
            </button>
          ) : (
            <button
              key="submit"
              type="submit"
              className="button button--primary button--block"
              disabled={submitCode.isPending || code.length !== OTP_LENGTH}
            >
              {submitCode.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          )}

          {step === 'email' && siteKey !== null && turnstileToken === '' ? (
            <p className="login__hint" role="status" key="hint">
              {turnstileStatus === 'failed'
                ? 'The check above has to pass before a code can be sent.'
                : turnstileStatus === 'loading'
                  ? 'Waiting for the human check to load.'
                  : 'Complete the human check to continue.'}
            </p>
          ) : null}

          {step === 'code' ? (
            <div className="login__secondary" key="secondary">
              <button
                type="button"
                className="button button--link"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  submitCode.reset();
                  requestCode.reset();
                }}
              >
                Use a different address
              </button>
              <button
                type="button"
                className="button button--link"
                disabled={cooldownSec > 0 || !canRequest}
                onClick={() => requestCode.mutate(email)}
              >
                {cooldownSec > 0 ? `Resend in ${cooldownSec}s` : 'Resend code'}
              </button>
            </div>
          ) : null}
        </form>

        {failure !== null ? (
          <p className="alert alert--danger" role="alert">
            {errorMessage(failure)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
